/**
 * Site Settings Management Page
 * 
 * Admin page to manage system-wide configuration settings
 */

import { useState, useEffect } from 'react';
import { adminApi } from '../stores/adminStore';
import { toast } from 'sonner';
import { 
  Save, 
  RefreshCw, 
  Settings as SettingsIcon,
  DollarSign,
  Upload,
  AlertTriangle,
  Eye,
  EyeOff,
  Monitor,
  Smartphone,
  Globe,
  Image as ImageIcon,
  Plus,
  Trash2,
  GripVertical
} from 'lucide-react';

interface SiteSetting {
  key: string;
  value: any;
  description?: string;
  category?: string;
  is_public?: boolean;
  created_at?: string;
  updated_at?: string;
}

interface LoginSlider {
  id: number;
  image_url: string;
  order: number;
}

interface LoginSlidersData {
  header_image?: string;
  sliders: LoginSlider[];
}

export default function SiteSettingsPage() {
  const [settings, setSettings] = useState<SiteSetting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editedValues, setEditedValues] = useState<Record<string, any>>({});
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loginSliders, setLoginSliders] = useState<LoginSlider[]>([]);
  const [headerImageUrl, setHeaderImageUrl] = useState('');
  const [newSliderUrl, setNewSliderUrl] = useState('');

  // Fetch all settings
  const fetchSettings = async () => {
    try {
      setIsLoading(true);

      console.log('Fetching settings from adminApi');

      const response = await adminApi.get('/api/v1/site-settings');

      console.log('Settings response:', response.data);

      if (response.data.success) {
        setSettings(response.data.data.settings || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch settings:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Full error object:', JSON.stringify(error.response?.data, null, 2));
      const errorMessage = typeof error.response?.data?.error === 'string' 
        ? error.response.data.error 
        : error.response?.data?.error?.message || error.message || 'Failed to load settings';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Update setting
  const updateSetting = async (key: string, value: any) => {
    try {
      setIsSaving(true);

      const response = await adminApi.put(
        `/api/v1/site-settings/${key}`,
        { value }
      );

      if (response.data.success) {
        toast.success(`Setting "${key}" updated successfully!`);
        // Refresh settings
        await fetchSettings();
        // Clear edited value
        const newEdited = { ...editedValues };
        delete newEdited[key];
        setEditedValues(newEdited);
      }
    } catch (error: any) {
      console.error('Failed to update setting:', error);
      const errorMessage = typeof error.response?.data?.error === 'string' 
        ? error.response.data.error 
        : error.message || 'Failed to update setting';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // Load login sliders when settings are fetched
  useEffect(() => {
    const slidersSetting = settings.find(s => s.key === 'login_sliders');
    if (slidersSetting) {
      const data = slidersSetting.value;
      
      // Handle both old format (array) and new format (object with sliders array)
      if (Array.isArray(data)) {
        // Old format - just array of sliders
        setLoginSliders(data);
        setHeaderImageUrl('');
      } else if (data && typeof data === 'object') {
        // New format - object with header_image and sliders
        setLoginSliders(data.sliders || []);
        setHeaderImageUrl(data.header_image || '');
      }
    }
  }, [settings]);

  // Save login sliders data (both header and sliders)
  const saveLoginSlidersData = async (sliders: LoginSlider[], headerImage?: string) => {
    const data: LoginSlidersData = {
      sliders: sliders,
      ...(headerImage && { header_image: headerImage })
    };
    await updateSetting('login_sliders', data);
  };

  // Update header image
  const updateHeaderImage = async () => {
    await saveLoginSlidersData(loginSliders, headerImageUrl.trim() || undefined);
    toast.success('Header image updated!');
  };

  // Add new slider
  const addSlider = async () => {
    if (!newSliderUrl.trim()) {
      toast.error('Please enter an image URL');
      return;
    }

    const newSlider: LoginSlider = {
      id: Date.now(),
      image_url: newSliderUrl.trim(),
      order: loginSliders.length + 1
    };

    const updatedSliders = [...loginSliders, newSlider];
    await saveLoginSlidersData(updatedSliders, headerImageUrl.trim() || undefined);
    setNewSliderUrl('');
  };

  // Delete slider
  const deleteSlider = async (id: number) => {
    const updatedSliders = loginSliders
      .filter(s => s.id !== id)
      .map((s, index) => ({ ...s, order: index + 1 }));
    await saveLoginSlidersData(updatedSliders, headerImageUrl.trim() || undefined);
  };

  // Move slider up
  const moveSliderUp = async (index: number) => {
    if (index === 0) return;
    const updatedSliders = [...loginSliders];
    [updatedSliders[index - 1], updatedSliders[index]] = [updatedSliders[index], updatedSliders[index - 1]];
    const reordered = updatedSliders.map((s, i) => ({ ...s, order: i + 1 }));
    await saveLoginSlidersData(reordered, headerImageUrl.trim() || undefined);
  };

  // Move slider down
  const moveSliderDown = async (index: number) => {
    if (index === loginSliders.length - 1) return;
    const updatedSliders = [...loginSliders];
    [updatedSliders[index], updatedSliders[index + 1]] = [updatedSliders[index + 1], updatedSliders[index]];
    const reordered = updatedSliders.map((s, i) => ({ ...s, order: i + 1 }));
    await saveLoginSlidersData(reordered, headerImageUrl.trim() || undefined);
  };

  // Get unique categories
  const categories = ['all', ...new Set(settings.map(s => s.category || 'general'))];

  // Filter settings by category
  const filteredSettings = selectedCategory === 'all' 
    ? settings 
    : settings.filter(s => (s.category || 'general') === selectedCategory);

  // Group settings by category
  const groupedSettings = filteredSettings.reduce((acc, setting) => {
    const cat = setting.category || 'general';
    if (!acc[cat]) {
      acc[cat] = [];
    }
    acc[cat].push(setting);
    return acc;
  }, {} as Record<string, SiteSetting[]>);

  // Get icon for category
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'credits':
        return <DollarSign className="w-5 h-5" />;
      case 'uploads':
        return <Upload className="w-5 h-5" />;
      case 'system':
        return <AlertTriangle className="w-5 h-5" />;
      case 'maintenance':
        return <AlertTriangle className="w-5 h-5 text-orange-600" />;
      default:
        return <SettingsIcon className="w-5 h-5" />;
    }
  };

  // Render maintenance mode card
  const renderMaintenanceCard = (key: string, title: string, Icon: any, iconClass: string) => {
    const setting = settings.find(s => s.key === key);
    if (!setting) return null;

    const value = setting.value || { enabled: false, message: '' };
    const isEnabled = value.enabled === true;
    
    // Use editedValues for message to allow editing
    const currentMessage = editedValues[key]?.message !== undefined 
      ? editedValues[key].message 
      : value.message || '';

    const updateMaintenance = async (newValue: { enabled: boolean; message: string }) => {
      try {
        setIsSaving(true);
        const response = await adminApi.put(`/api/v1/site-settings/${key}`, { value: newValue });
        if (response.data.success) {
          toast.success(`${title} updated!`);
          await fetchSettings();
          // Clear edited value after successful save
          const newEdited = { ...editedValues };
          delete newEdited[key];
          setEditedValues(newEdited);
        }
      } catch (error: any) {
        toast.error(error.response?.data?.error || 'Failed to update');
      } finally {
        setIsSaving(false);
      }
    };

    const handleMessageChange = (newMessage: string) => {
      setEditedValues({
        ...editedValues,
        [key]: { enabled: isEnabled, message: newMessage }
      });
    };

    return (
      <div className={`border-2 rounded-lg p-4 ${isEnabled ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-2 rounded-lg ${iconClass}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">{title}</h3>
            <p className={`text-xs ${isEnabled ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
              {isEnabled ? 'Maintenance Active' : 'Online'}
            </p>
          </div>
          <button
            onClick={() => updateMaintenance({ enabled: !isEnabled, message: currentMessage })}
            disabled={isSaving}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
              isEnabled ? 'bg-red-600' : 'bg-gray-300'
            } ${isSaving ? 'opacity-50' : ''}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
              isEnabled ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Message (supports HTML)</label>
          <textarea
            value={currentMessage}
            onChange={(e) => handleMessageChange(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            placeholder="Enter maintenance message (HTML supported)..."
          />
          <p className="text-xs text-gray-500 mt-1">Use HTML tags like &lt;b&gt;, &lt;br/&gt;, &lt;a href=""&gt;</p>
        </div>

        <button
          onClick={() => updateMaintenance({ enabled: isEnabled, message: currentMessage })}
          disabled={isSaving}
          className="w-full mt-3 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
        >
          {isSaving ? (
            <>
              <RefreshCw className="w-3 h-3 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-3 h-3" />
              Save
            </>
          )}
        </button>
      </div>
    );
  };

  // Render input based on value type
  const renderInput = (setting: SiteSetting) => {
    const currentValue = editedValues[setting.key] !== undefined 
      ? editedValues[setting.key] 
      : setting.value;

    const handleChange = (newValue: any) => {
      setEditedValues({
        ...editedValues,
        [setting.key]: newValue
      });
    };

    // Number input
    if (typeof setting.value === 'number') {
      return (
        <input
          type="number"
          value={currentValue}
          onChange={(e) => handleChange(parseFloat(e.target.value) || 0)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      );
    }

    // Boolean toggle
    if (typeof setting.value === 'boolean') {
      return (
        <label className="flex items-center space-x-3 cursor-pointer">
          <div className="relative">
            <input
              type="checkbox"
              checked={currentValue}
              onChange={(e) => handleChange(e.target.checked)}
              className="sr-only"
            />
            <div className={`block w-14 h-8 rounded-full transition ${currentValue ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
            <div className={`absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition transform ${currentValue ? 'translate-x-6' : ''}`}></div>
          </div>
          <span className="text-sm font-medium text-gray-700">
            {currentValue ? 'Enabled' : 'Disabled'}
          </span>
        </label>
      );
    }

    // String input
    return (
      <input
        type="text"
        value={currentValue}
        onChange={(e) => handleChange(e.target.value)}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center space-y-4">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <SettingsIcon className="w-7 h-7 text-blue-600" />
            Site Settings
          </h1>
          <p className="text-gray-600 mt-1">
            Manage system-wide configuration and behavior
          </p>
        </div>
        <button
          onClick={fetchSettings}
          className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-4 py-2 rounded-lg font-medium transition whitespace-nowrap ${
              selectedCategory === category
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {category.charAt(0).toUpperCase() + category.slice(1)}
          </button>
        ))}
      </div>

      {/* Maintenance Mode Section - Special UI */}
      {selectedCategory === 'maintenance' || selectedCategory === 'all' ? (
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
              <div>
                <h2 className="text-xl font-bold text-gray-900">Maintenance Mode Control</h2>
                <p className="text-sm text-gray-600">Toggle maintenance mode for different platforms</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Web */}
              {renderMaintenanceCard('maintenance_web', 'Web Application', Globe, 'bg-blue-100 text-blue-600')}
              
              {/* Desktop */}
              {renderMaintenanceCard('maintenance_desktop', 'Desktop App', Monitor, 'bg-purple-100 text-purple-600')}
              
              {/* Mobile */}
              {renderMaintenanceCard('maintenance_mobile', 'Mobile App', Smartphone, 'bg-green-100 text-green-600')}
            </div>
          </div>
        </div>
      ) : null}

      {/* Login Sliders Section */}
      {(selectedCategory === 'mobile' || selectedCategory === 'all') && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <ImageIcon className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Login Screen Sliders (Mobile)</h2>
              <p className="text-sm text-gray-600">
                Manage background images for mobile login screen. Multiple images enable swipe slider.
              </p>
            </div>
          </div>

          {/* Header Image Section */}
          <div className="mb-6 p-4 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border-2 border-purple-200">
            <div className="flex items-start gap-3 mb-3">
              <ImageIcon className="w-5 h-5 text-purple-600 mt-1" />
              <div className="flex-1">
                <label className="block text-sm font-semibold text-gray-900 mb-1">
                  Header Image (Optional)
                </label>
                <p className="text-xs text-gray-600 mb-3">
                  Static logo/branding that appears above sliders. Recommended: 1200x900px PNG with transparent background.
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={headerImageUrl}
                    onChange={(e) => setHeaderImageUrl(e.target.value)}
                    placeholder="Enter header image URL (leave empty for no header)"
                    className="flex-1 px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                  />
                  <button
                    onClick={updateHeaderImage}
                    disabled={isSaving}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="w-4 h-4" />
                    Save
                  </button>
                </div>
                {headerImageUrl && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-gray-700 mb-2">Preview:</p>
                    <img
                      src={headerImageUrl}
                      alt="Header preview"
                      className="max-w-xs h-32 object-contain bg-gray-100 rounded-lg border border-gray-300"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/300x200?text=Invalid+URL';
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Add New Slider */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Add New Slider Image
            </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newSliderUrl}
                  onChange={(e) => setNewSliderUrl(e.target.value)}
                  placeholder="Enter image URL (e.g., https://images.unsplash.com/...)"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={addSlider}
                  disabled={isSaving || !newSliderUrl.trim()}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                💡 Tip: Upload images to your CDN first, then paste the URL here
              </p>
            </div>

            {/* Sliders List */}
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-700">
                  Current Sliders ({loginSliders.length})
                </h3>
                {loginSliders.length > 1 && (
                  <span className="text-xs text-green-600 font-medium">
                    ✓ Slider mode active
                  </span>
                )}
              </div>

              {loginSliders.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No sliders added yet</p>
                </div>
              ) : (
                loginSliders.map((slider, index) => (
                  <div
                    key={slider.id}
                    className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:shadow-md transition"
                  >
                    {/* Preview */}
                    <img
                      src={slider.image_url}
                      alt={`Slider ${index + 1}`}
                      className="w-20 h-20 object-cover rounded-lg"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/80?text=Error';
                      }}
                    />

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-gray-900">
                          Slider #{slider.order}
                        </span>
                        {index === 0 && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                            First
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {slider.image_url}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      {/* Move Up */}
                      <button
                        onClick={() => moveSliderUp(index)}
                        disabled={index === 0 || isSaving}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move up"
                      >
                        <GripVertical className="w-4 h-4 rotate-90" />
                      </button>

                      {/* Move Down */}
                      <button
                        onClick={() => moveSliderDown(index)}
                        disabled={index === loginSliders.length - 1 || isSaving}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move down"
                      >
                        <GripVertical className="w-4 h-4 -rotate-90" />
                      </button>

                      {/* Delete */}
                      <button
                        onClick={() => {
                          if (confirm(`Delete slider #${slider.order}?`)) {
                            deleteSlider(slider.id);
                          }
                        }}
                        disabled={isSaving}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition disabled:opacity-50"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Info */}
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                <strong>How it works:</strong> If you have only 1 image, it will display as a static background. 
                If you have 2+ images, users can swipe left/right with dot indicators showing which slide is active.
              </p>
            </div>
        </div>
      )}

      {/* Regular Settings Grid */}
      {Object.entries(groupedSettings)
        .filter(([cat]) => cat !== 'maintenance')
        .map(([category, categorySettings]) => {
          // Filter out login_sliders from regular settings (it has its own special section)
          const filteredSettings = categorySettings.filter(s => s.key !== 'login_sliders');
          
          // Skip rendering if no settings left after filtering
          if (filteredSettings.length === 0) return null;
          
          return (
            <div key={category} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Category Header */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  {getCategoryIcon(category)}
                  <h2 className="text-lg font-semibold text-gray-900 capitalize">
                    {category} Settings
                  </h2>
                  <span className="ml-auto text-sm text-gray-500">
                    {filteredSettings.length} setting{filteredSettings.length > 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              {/* Settings List */}
              <div className="divide-y divide-gray-200">
                {filteredSettings.map((setting) => {
              const hasChanges = editedValues[setting.key] !== undefined;
              
              return (
                <div key={setting.key} className="p-6 hover:bg-gray-50 transition">
                  <div className="flex items-start justify-between gap-6">
                    {/* Setting Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-sm font-semibold text-gray-900">
                          {setting.key}
                        </h3>
                        {setting.is_public && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                            <Eye className="w-3 h-3" />
                            Public
                          </span>
                        )}
                        {!setting.is_public && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                            <EyeOff className="w-3 h-3" />
                            Private
                          </span>
                        )}
                      </div>
                      {setting.description && (
                        <p className="text-sm text-gray-600 mb-3">
                          {setting.description}
                        </p>
                      )}
                      
                      {/* Input */}
                      <div className="max-w-md">
                        {renderInput(setting)}
                      </div>

                      {/* Meta Info */}
                      <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                        <span>
                          Current: <span className="font-mono font-semibold">{JSON.stringify(setting.value)}</span>
                        </span>
                        {setting.updated_at && (
                          <span>
                            Updated: {new Date(setting.updated_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      {hasChanges && (
                        <>
                          <button
                            onClick={() => updateSetting(setting.key, editedValues[setting.key])}
                            disabled={isSaving}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                          >
                            <Save className="w-4 h-4" />
                            Save
                          </button>
                          <button
                            onClick={() => {
                              const newEdited = { ...editedValues };
                              delete newEdited[setting.key];
                              setEditedValues(newEdited);
                            }}
                            className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
          );
        }
      )}

      {/* Empty State */}
      {filteredSettings.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <SettingsIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No settings found
          </h3>
          <p className="text-gray-600">
            {selectedCategory === 'all' 
              ? 'No settings have been configured yet.'
              : `No settings found in the "${selectedCategory}" category.`
            }
          </p>
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-blue-900 mb-1">
              Important Notes
            </h4>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Changes take effect immediately after saving</li>
              <li>Public settings can be accessed without authentication</li>
              <li>Be careful when modifying system-critical settings</li>
              <li>Free credits amount applies to all new user signups</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
